import type { CallToAction, UpdateAdInput } from '../types';

/**
 * The typed INTENT the ad side-panel holds: the editable creative fields plus
 * whether the messaging destination or lead-form CTA is locked.
 * Lead forms still need an editable external website URL.
 */
export interface UpdateAdIntent {
  name: string;
  leadFormId?: string;
  headline?: string;
  primaryText?: string;
  description?: string;
  callToAction?: string;
  destinationUrl?: string;
  destinationLocked: boolean;
  callToActionLocked?: boolean;
}

/** The fields this builder can put on the wire. */
export type UpdateAdField = keyof Omit<
  UpdateAdIntent,
  'destinationLocked' | 'callToActionLocked'
>;

/**
 * THE update-ad payload builder. The side-panel passes intent; the wire body is
 * assembled here. When the destination is locked, CTA + URL are omitted (they'd
 * have no effect); an edited empty URL explicitly clears the saved destination.
 *
 * ONLY THE FIELDS THAT CHANGED GO ON THE WIRE, and that is load-bearing rather
 * than tidiness. `updateAdImpl` decides whether to rebuild the ad's creative by
 * asking whether any of `headline`, `primaryText`, `description`,
 * `callToAction` or `destinationUrl` is `!== undefined` — not whether its value
 * differs. Sending all five on every save therefore made EVERY edit a creative
 * rebuild: renaming a live ad minted a fresh creative, swapped it onto the
 * running ad and sent it back through Meta's review, pausing delivery for a
 * change that never touched the creative. Observed in a browser — a rename
 * moved `metaCreativeId` from `…8065656f` to `…94dad8aa`.
 *
 * `changed` is REQUIRED for that reason: an omitted set would quietly restore
 * the old behaviour at the one call site that matters.
 */
export function buildUpdateAdPayload(
  input: UpdateAdIntent,
  changed: readonly UpdateAdField[]
): UpdateAdInput {
  const touched = new Set(changed);
  // Untouched fields must not overwrite changes made while the form was open.
  const payload: UpdateAdInput = {};
  if (touched.has('name')) payload.name = input.name;

  if (touched.has('leadFormId')) payload.leadFormId = input.leadFormId;
  if (touched.has('headline')) payload.headline = input.headline;
  if (touched.has('primaryText')) payload.primaryText = input.primaryText;
  if (touched.has('description')) payload.description = input.description;

  if (
    !(input.callToActionLocked ?? input.destinationLocked) &&
    touched.has('callToAction')
  )
    payload.callToAction = (input.callToAction as CallToAction) || undefined;
  if (!input.destinationLocked) {
    if (touched.has('destinationUrl'))
      payload.destinationUrl = input.destinationUrl || null;
  }

  return payload;
}

/** A change to what an ad promotes. `undefined` leaves that link alone. */
export interface AdLinksIntent {
  /** The whole new service set; `[]` unlinks every service. */
  serviceIds?: string[];
  /** The promotion to link, or `null` to unlink it. */
  offerId?: string | null;
}

/**
 * The wire body for a links-only save. It carries `serviceIds` / `offerId`
 * and NOTHING else — not even `name`. The server pushes a live ad to Meta when
 * any content field is present, so a link change that smuggled one in would
 * re-sync (or re-review) a running ad for a change that is Borradh-only.
 */
export function buildAdLinksPayload(input: AdLinksIntent): UpdateAdInput {
  const payload: UpdateAdInput = {};
  if (input.serviceIds !== undefined)
    payload.serviceIds = [...new Set(input.serviceIds)];
  if (input.offerId !== undefined) payload.offerId = input.offerId;
  return payload;
}
